/* eslint-disable spire/export-styles */
import { EMEARegion, UserTypeCustomer } from "@dana/Common/Constants";
import useIsNA from "@dana/Common/Hooks/useIsNA";
import MinimumOrderQtyMessage from "@dana/Components/MinimumOrderQtyMessage";
import MultipleSaleQtyMessage from "@dana/Components/MultipleSaleQtyMessage";
import ProductDetailsInternalUseOnlyMessage from "@dana/Components/ProductDetails/ProductDetailsInternalUseOnlyMessage";
import ProductDetailsOEMPartNotFoundMessage from "@dana/Components/ProductDetails/ProductDetailsOEMPartNotFoundMessage";
import ProductDetailsSalesRepModel from "@dana/Components/ProductDetails/ProductDetailsSalesRepModel";
import { OrderTypes } from "@dana/Handlers/ProductAvailabilityHandler";
import CircleInfo from "@dana/Icons/CircleInfo";

import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import loadEmergencyCart from "@dana/Store/Data/EmergencyCart/EmergencyCartHandler";
import resetSelectedInventryLocations from "@dana/Store/Data/ProductAvailability/Handlers/ResetSelectedInventryLocationsHandler";
import ProductAvailabilityState from "@dana/Store/Data/ProductAvailability/ProductAvailabilityState";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, ProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconMemo } from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface ProductAddToCartButtonStyles {
    button?: ButtonPresentationProps;
    messageStyles?: InjectableCss;
    messageStylesWrapper?: InjectableCss;
}
interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToCartButtonStyles;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    orderType?: string;
    isEditMode?: boolean;
    onAddToCartClick?: () => boolean;
    productAvilabilityModel?: ProductAvailabilityState;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationStateDana, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    const locationsState = state.selectedInventryLocationsState;
    const currentLanguage = state.context.session.language;
    const isNARegion = useIsNA(state);


    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        orderType: props.orderType,
        locationsState,
        currentLanguage,
        productAvilabilityModel: props.productAvilabilityModel,
        userType: state.context.session?.properties?.userType ?? "",
        isNARegion
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    addLinesToCart,
    resetSelectedInventryLocations
};

export const productAddToCartButtonStyles: ProductAddToCartButtonStyles = {};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired, minimumOrderQty, detail, properties },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    orderType,
    productAvilabilityModel,
    isEditMode = false,
    addLinesToCart,
    locationsState,
    currentLanguage,
    resetSelectedInventryLocations,
    userType,
    isNARegion,
    ...otherProps
}: Props) => {

    const [crossref, setCrossref] = React.useState<string | null>(null);
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const [unitPriceDisplay, setUnitPriceDisplay] = useState('0.00');
    const showAddToCart = properties?.showRtbCodeAddToCart;
    const isOEMPart = properties?.isOEMPart;
    const requestedShiptDate = properties?.requestedShipDate;

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }
    const [unitPrice, setUnitPrice] = useState(0);
    const soldQty = detail?.multipleSaleQty;
    const minQty = minimumOrderQty;

    React.useEffect(() => {
        if(isNARegion)
        {
            const queryParams = new URLSearchParams(window.location.search);
            const crossrefValue = queryParams.get("crossReferance");
            if (crossrefValue !== null && crossrefValue.trim() !== "") {
            setCrossref(crossrefValue);
            } else {
            setCrossref(null);
            }
       }
    }, []);

    React.useEffect(() => {
        setUnitPrice(pricing?.unitCost || 0);
        setUnitPriceDisplay(pricing?.unitCostDisplay || '0.00');

    }, [pricing]);
    const addToCartClickHandler = async () => {
        if (onAddToCartClick) {
            const result = onAddToCartClick();
            if (result) {
                return;
            }
        }

        const formatDate = (isoString: string): string => {
            const date = new Date(isoString);
            return new Intl.DateTimeFormat(currentLanguage?.languageCode).format(date);
        };
        const currentType = orderType ?? getCookie('modalOrderType') ?? OrderTypes.STOCK;
        setCookie("modalOrderType", currentType);

        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }
        let stockPrice = pricing?.additionalResults?.erpResponse;
        let emeregencyPrice = pricing?.additionalResults?.emergencyErpResponse;
        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                    .filter(sectionOptionId => !!sectionOptionId)
                    .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const notePrice  = currentType  === OrderTypes.STOCK  ? stockPrice: emeregencyPrice;
        if (locationsState.productId === productId && locationsState.selectedLocations?.length) {
            const cartLines: CartLineModel[] = [];
            locationsState.selectedLocations.forEach(o => {
                const location = o.location?.properties?.plantCode ? o.location?.properties?.plantCode : currentLanguage?.languageCode?.toLocaleLowerCase() === "en-us" ? "1601" : "295";
                cartLines.push({
                    productId,                    
                    qtyOrdered: (o.selectedQuantity && o.selectedQuantity <= o.committedQuantity) ? o.selectedQuantity : o.committedQuantity,
                    properties: {
                        location,
                        estimatedShipDate: o.committedDate ? formatDate(o.committedDate) : "",
                    } as any,
                    notes:crossref !== null && crossref !== ""? crossref+'♦'+notePrice:notePrice                   
                } as CartLineModel);
            });

            addLinesToCart({
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: { cartLines } as CartLineCollectionModel,
                },
                onSuccess: () => {
                    if (showAddToCartConfirmationDialog) {
                        toasterContext.addToast({
                            body: translate("Lines added to the cart."),
                            messageType: "success",
                            isFocusAfterMount: true,
                        });
                        resetSelectedInventryLocations({});
                    }
                    if (currentType === OrderTypes.EMERGENCY) {
                        loadEmergencyCart({
                            cartId: API_URL_CURRENT_FRAGMENT,
                        });
                    }
                }
            });
        } else {
            const cartline = (await addToCart({
                productId,
                qtyOrdered,
                unitOfMeasure,      
                sectionOptions,
                subscription, 
                notes:crossref !== null && crossref !== ""? crossref+'♦'+notePrice:notePrice                       
            })) as CartLineModel;

            if (showAddToCartConfirmationDialog) {
                toasterContext.addToast({
                    body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                    messageType: "success",
                    timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
                });
                resetSelectedInventryLocations({});
            }
            if (currentType === OrderTypes.EMERGENCY) {
                loadEmergencyCart({
                    cartId: API_URL_CURRENT_FRAGMENT,
                });
            }
        }
    };

    return (
        <StyledWrapper {...styles.messageStylesWrapper}>
            {showAddToCart === "true" ? (
                (isEditMode === false && currentLanguage && currentLanguage.properties.region === EMEARegion && (unitPrice === 0 && notes !== "mylist")) || (isEditMode === true && currentLanguage && currentLanguage.properties.region === EMEARegion && (productAvilabilityModel?.productCustomerPrice?.unitListPrice === 0 && notes !== "mylist")) ?
                    <StyledWrapper {...styles.messageStyles}>
                        <ProductDetailsSalesRepModel
                            salesRepDetails={properties?.salesRepDetails}
                        ></ProductDetailsSalesRepModel>
                    </StyledWrapper>
                    :
                    <>
                        <StyledWrapper {...styles.messageStyles}>
                            <MultipleSaleQtyMessage />
                            <MinimumOrderQtyMessage />
                        </StyledWrapper>

                        <Button
                            {...styles.button}
                            onClick={addToCartClickHandler}
                            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
                            {...otherProps}
                        >
                            {labelOverride ?? translate("Add to Cart")}
                        </Button>
                    </>
            ) : isOEMPart === "true" ?
                (
                    <StyledWrapper {...styles.messageStyles}>
                        <ProductDetailsOEMPartNotFoundMessage></ProductDetailsOEMPartNotFoundMessage>
                    </StyledWrapper>
                )
                : (!showAddToCart && isNARegion && properties?.rtbCode === "P09") ? (
                    <StyledWrapper {...styles.messageStyles}>
                        <ProductDetailsInternalUseOnlyMessage></ProductDetailsInternalUseOnlyMessage>
                    </StyledWrapper>

                ) : (
                    <StyledWrapper {...styles.messageStyles}>
                        <ProductDetailsSalesRepModel
                            salesRepDetails={properties?.salesRepDetails}
                        ></ProductDetailsSalesRepModel>
                    </StyledWrapper>
                )}
        </StyledWrapper>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
